import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lightning, Lock, CheckCircle, Warning } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated — please sign in");
      setTimeout(() => nav("/auth"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 relative overflow-hidden" data-testid="reset-password-page">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#FF3B6B]/20 blur-[120px] rounded-full" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#00E5FF]/10 blur-[100px] rounded-full" />
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="relative w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8" data-testid="reset-logo">
          <div className="w-9 h-9 rounded-md bg-[#FF3B6B] flex items-center justify-center shadow-[0_0_24px_-4px_#FF3B6B]">
            <Lightning weight="fill" className="text-white" size={20} />
          </div>
          <span className="font-semibold text-xl">ClipForge<span className="text-[#FF3B6B]">AI</span></span>
        </Link>
        <div className="glass-strong rounded-2xl p-8">
          {!token ? (
            <div className="text-center" data-testid="reset-no-token">
              <Warning size={48} weight="duotone" className="mx-auto text-[#EAB308] mb-4" />
              <h1 className="text-2xl font-bold tracking-tighter">Invalid reset link</h1>
              <p className="text-sm text-white/60 mt-2">The reset link is missing a token. Request a new one.</p>
              <Link to="/forgot-password" className="inline-block mt-6 btn-primary px-5 py-2.5 rounded-lg text-sm font-semibold" data-testid="reset-request-new">
                Request new link
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold tracking-tighter">Set a new password</h1>
              <p className="text-sm text-white/50 mt-1">Choose something strong — minimum 6 characters.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs text-white/60 mb-1.5 block">New password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      autoFocus
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-10 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none transition-colors"
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-white/60 mb-1.5 block">Confirm password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                    <input
                      type="password"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      required
                      minLength={6}
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-10 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none transition-colors"
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>
                <button
                  disabled={loading}
                  type="submit"
                  className="w-full btn-primary py-3 rounded-lg font-semibold disabled:opacity-50"
                  data-testid="reset-submit-btn"
                >
                  {loading ? "Updating..." : "Update password"}
                </button>
              </form>
              {password && confirm && password !== confirm && (
                <p className="mt-3 text-xs text-[#FF3B6B] flex items-center gap-1.5" data-testid="reset-mismatch">
                  <Warning size={12} weight="fill" /> Passwords don't match
                </p>
              )}
              {password && password.length > 0 && password.length < 6 && (
                <p className="mt-3 text-xs text-[#EAB308] flex items-center gap-1.5">
                  <CheckCircle size={12} weight="fill" /> Use at least 6 characters
                </p>
              )}
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
